// One catalog evaluation shared by the Firefly tab glow and the open panel.
import { useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useAnchorEntitlements } from "@/contexts/AnchorEntitlementsContext";
import { useWorkspaceUrgencyContext } from "@/contexts/WorkspaceUrgencyContext";
import { resolveAdsHeaderActivityPill } from "@/components/ads-assistant/resolveAdsHeaderActivityPill";
import { canAccessPrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { getBucketForMode } from "@/components/shell/modes/bucketsRegistry";
import { canAccessByAnchor } from "@/lib/entitlements/modeAnchorRequirement";
import { listAttentionItems, type AttentionList } from "@/lib/attention/attentionCatalog";
import { useActivityDockSections } from "@/components/activity/useActivityDockSections";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";

export type UseAttentionListArgs = {
  currentMode: PrimaryMode | null;
  openQcCount: number;
  negativeOnHandCount: number;
  plantRequestReviewCount: number;
  plantStatusCount: number;
  staleWacCount: number;
  kbGapOpenCount: number;
  aiPromptingOpenCount: number;
  pendingMemberApprovalTotal: number;
  orgUnsentRollupCount: number;
  onOpenLeads: boolean;
  onOpenBuilderAssistant: boolean;
  onOpenInventory: boolean;
  onOpenPurchasing: boolean;
  onOpenAds: boolean;
  onOpenKb: boolean;
  onOpenAiPrompting: boolean;
  onOpenAdmin: boolean;
};

export function useAttentionList(args: UseAttentionListArgs): AttentionList & { unsentCount: number } {
  const auth = useAuth();
  const { anchors } = useAnchorEntitlements();
  const urgency = useWorkspaceUrgencyContext();
  const bucket = args.currentMode ? getBucketForMode(args.currentMode) : null;
  const sections = useActivityDockSections(bucket);
  const unsentCount = (urgency?.leads ?? []).reduce(
    (n, lead) => (!lead.sent && !lead.isTest ? n + 1 : n),
    0,
  );
  const adsTone = useMemo(() => {
    const pill = resolveAdsHeaderActivityPill(urgency?.adsActivity ?? null, auth);
    return pill?.visible ? pill.tone : null;
  }, [auth, urgency?.adsActivity]);

  const list = useMemo(
    () =>
      listAttentionItems({
        auth,
        sectionIds: sections.map((section) => section.id),
        canOpenMode: (mode) =>
          canAccessPrimaryMode(mode, auth) && canAccessByAnchor(mode, anchors),
        hasOpenCallback: {
          unsent_leads: args.onOpenLeads,
          open_qc: args.onOpenBuilderAssistant,
          negative_on_hand: args.onOpenInventory,
          plant_material_request: args.onOpenPurchasing,
          ads_goal: args.onOpenAds,
          stale_wac: args.onOpenInventory,
          kb_gaps: args.onOpenKb,
          ai_prompting_suggestions: args.onOpenAiPrompting,
          pending_approvals: args.onOpenAdmin,
        },
        unsentCount,
        openQcCount: args.openQcCount,
        negativeOnHandCount: args.negativeOnHandCount,
        plantRequestReviewCount: args.plantRequestReviewCount,
        plantStatusCount: args.plantStatusCount,
        staleWacCount: args.staleWacCount,
        kbGapOpenCount: args.kbGapOpenCount,
        aiPromptingOpenCount: args.aiPromptingOpenCount,
        pendingMemberApprovalTotal: args.pendingMemberApprovalTotal,
        orgUnsentRollupCount: args.orgUnsentRollupCount,
        adsTone,
      }),
    [adsTone, anchors, args, auth, sections, unsentCount],
  );

  return { ...list, unsentCount };
}
